import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import {
  IncidentSchemaPriority,
  IncidentSchemaState,
} from 'src/app/models/incident-schema';
import { GQLOffsetPaginationMetadata } from 'src/app/models/shared';

interface GQLIncidentToastSummary {
  title: string;
  state: IncidentSchemaState | null;
  priority: IncidentSchemaPriority | null;
  schemaVersion: {
    states: IncidentSchemaState[];
    priorities: IncidentSchemaPriority[];
  } | null;
}

/**
 * Same `incidents` query the list pages use, stripped down to the pagination
 * metadata — the count is all we need, so no incident fields are selected.
 */
export const ACTIVE_INCIDENT_COUNT_QUERY = gql`
  query GetActiveIncidentCount(
    $organizationUUID: ID!
    $listIncidentsRequest: ListIncidentsRequest
  ) {
    incidents(
      organizationUUID: $organizationUUID
      listIncidentsRequest: $listIncidentsRequest
    ) {
      paginationMetadata {
        totalCount
      }
    }
  }
`;

const SCHEMA_STATE_FIELDS = `
  id
  name
  order
  color
  icon
  isDefault
  isResolved
`;

const SCHEMA_PRIORITY_FIELDS = `
  id
  name
  order
  color
  icon
`;

/** What a realtime toast shows beyond the event: events carry only the
 *  incident's number and the changed field. */
export const INCIDENT_TOAST_SUMMARY_QUERY = gql`
  query GetIncidentToastSummary($organizationUUID: ID!, $id: Int!) {
    incident(organizationUUID: $organizationUUID, id: $id) {
      id
      title
      state { ${SCHEMA_STATE_FIELDS} }
      priority { ${SCHEMA_PRIORITY_FIELDS} }
      schemaVersion {
        id
        states { ${SCHEMA_STATE_FIELDS} }
        priorities { ${SCHEMA_PRIORITY_FIELDS} }
      }
    }
  }
`;

export interface IncidentToastSummary {
  title: string;
  state?: IncidentSchemaState;
  priority?: IncidentSchemaPriority;
  states: IncidentSchemaState[];
  priorities: IncidentSchemaPriority[];
}

@Injectable()
export class IncidentActivityService {
  private apollo = inject(Apollo);

  /** Number of unresolved incidents in the organization. */
  activeIncidentCount(organizationId: string): Observable<number> {
    return this.apollo
      .query<{
        incidents: { paginationMetadata: GQLOffsetPaginationMetadata };
      }>({
        query: ACTIVE_INCIDENT_COUNT_QUERY,
        variables: {
          organizationUUID: organizationId,
          listIncidentsRequest: {
            // 3 is the smallest page size the backend's pagination
            // validation accepts; only the metadata is read anyway
            pagination: { page: 1, pageSize: 3 },
            query: 'resolved = false',
          },
        },
        // Always hit the network — the whole point of a refresh is a fresh
        // count, and with no entity fields selected there is nothing worth
        // normalizing into the Apollo cache anyway.
        fetchPolicy: 'no-cache',
      })
      .pipe(map(data => data.data!.incidents.paginationMetadata.totalCount));
  }

  incidentToastSummary(
    organizationId: string,
    incidentId: number
  ): Observable<IncidentToastSummary> {
    return this.apollo
      .query<{ incident: GQLIncidentToastSummary }>({
        query: INCIDENT_TOAST_SUMMARY_QUERY,
        variables: { organizationUUID: organizationId, id: incidentId },
        // the event may be the very change being shown, so never read a
        // cached state/title
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(({ data }) => {
          const incident = data!.incident;
          return {
            title: incident.title,
            state: incident.state ?? undefined,
            priority: incident.priority ?? undefined,
            states: incident.schemaVersion?.states ?? [],
            priorities: incident.schemaVersion?.priorities ?? [],
          };
        })
      );
  }
}
